import { useEffect, useState } from 'react';
import { Underline } from '../../../components/doodles/Doodles.js';
import { speakText, practicePronunciation } from '../../../lib/englishApi.js';
import { useAudioBlob } from '../../../lib/audioBlobPlayer.js';
import { useMicWavCapture } from '../../../lib/micWavCapture.js';
import { useSfx } from '../../../lib/sfx/index.js';
import { IconCheck, IconClose, IconMicSolid, IconPlay, IconWaveSmall } from '../icons.js';
import type { ConvError } from '../errorModel.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';

const TOTAL_ATTEMPTS = 5;

type DrillPhase = 'idle' | 'recording' | 'scoring' | 'feedback';

interface Attempt {
  score: number;
  label: string;
}

function labelFor(score: number): string {
  if (score >= 92) return '¡Perfecto!';
  if (score >= 80) return '¡Muy bien!';
  if (score >= 70) return 'Cerca';
  return 'Sigue intentando';
}

function toneFor(score: number): 'good' | 'ok' | 'low' {
  if (score >= 80) return 'good';
  if (score >= 70) return 'ok';
  return 'low';
}

interface DrillModalProps {
  err: ConvError;
  onClose: () => void;
  sessionId?: string;
  /** Pausa/reanuda la captura del mic de la sesión principal durante el drill. */
  pauseMainCapture: () => void;
  resumeMainCapture: () => void;
}

/**
 * Drill de pronunciación con scoring REAL (`POST /pa-practice`). Grabá la
 * palabra (WAV PCM16 mono 16k), el backend devuelve accuracy 0–100 y se mapea
 * a score/etiqueta. 5 intentos llenan los pips.
 */
export function DrillModal({ err, onClose, sessionId, pauseMainCapture, resumeMainCapture }: DrillModalProps) {
  const { grabProps, sheetStyle } = useSheetDrag(onClose);
  const [phase, setPhase] = useState<DrillPhase>('idle');
  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [playingSyll, setPlayingSyll] = useState<number | null>(null);

  const wordAudio = useAudioBlob();
  const syllAudio = useAudioBlob();
  const mic = useMicWavCapture();
  const { play } = useSfx();

  const done = attempts.length >= TOTAL_ATTEMPTS;
  const best = attempts.reduce((m, a) => Math.max(m, a.score), 0);
  const lastAttempt = attempts[attempts.length - 1];

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Cleanup defensivo: si se desmonta mientras grababa, reanudar la captura.
  useEffect(() => () => resumeMainCapture(), [resumeMainCapture]);

  const playFull = (): void => {
    if (phase === 'recording') return;
    void wordAudio.toggle(() => speakText(err.word, sessionId ? { sessionId } : {}));
  };

  const playSyll = (i: number, text: string): void => {
    setPlayingSyll(i);
    void syllAudio
      .toggle(() => speakText(text, sessionId ? { sessionId } : {}))
      .finally(() => setPlayingSyll(null));
  };

  const startRecord = async (): Promise<void> => {
    if (phase === 'recording' || phase === 'scoring' || done) return;
    setError(null);
    // Bloqueamos el envío de PCM al WS de la sesión: el drill es aislado.
    pauseMainCapture();
    let blob: Blob | null = null;
    setPhase('recording');
    try {
      blob = await mic.start();
    } finally {
      resumeMainCapture();
    }
    if (!blob) {
      setPhase('idle');
      setError(mic.error ?? 'No pudimos capturar audio.');
      return;
    }
    setPhase('scoring');
    try {
      const r = await practicePronunciation(err.word, blob);
      const score = Math.round(r.accuracy);
      setAttempts((prev) => [...prev, { score, label: labelFor(score) }]);
      setPhase('feedback');
      const willBeDone = attempts.length + 1 >= TOTAL_ATTEMPTS;
      play(willBeDone ? 'drill-complete' : score >= 70 ? 'correct' : 'almost');
    } catch (e) {
      setPhase('idle');
      setError(e instanceof Error ? e.message : 'No pudimos evaluar el audio.');
    }
  };

  return (
    <div className="drill-overlay" onClick={onClose}>
      <div className="drill-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="drill-handle sheet-grab" {...grabProps} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 22px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: 10, background: 'rgba(251,191,36,0.18)', color: '#fbbf24', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconWaveSmall size={13} />
            </div>
            <div>
              <span className="tag" style={{ color: '#fbbf24' }}>
                DRILL
              </span>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-faint)', marginTop: 1 }}>
                pronunciación · {TOTAL_ATTEMPTS} intentos
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{ width: 32, height: 32, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          >
            <IconClose size={14} />
          </button>
        </div>

        {/* Palabra */}
        <div style={{ padding: '20px 22px 8px', textAlign: 'center', position: 'relative' }}>
          <div style={{ fontFamily: 'Caveat, cursive', fontWeight: 600, fontSize: 56, lineHeight: 1, color: 'var(--text)', display: 'inline-block', position: 'relative' }}>
            {err.word}
            <Underline color="#fbbf24" width={Math.min(220, err.word.length * 22)} style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: -8 }} />
          </div>
          {err.ipa && (
            <div style={{ marginTop: 16, fontFamily: 'JetBrains Mono, monospace', fontSize: 16, color: 'var(--lime)' }}>{err.ipa}</div>
          )}
        </div>

        {/* Desglose silábico (si el backend lo provee) */}
        {err.syllables && err.syllables.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 10, padding: '18px 22px 4px' }}>
            {err.syllables.map((s, i) => (
              <button
                key={i}
                className={`syll-chip ${s.stress === 'primary' ? 'is-primary' : ''} ${playingSyll === i ? 'is-playing' : ''}`}
                onClick={() => playSyll(i, s.text)}
              >
                <div className="syll-text">{s.text}</div>
                <div className="syll-ipa">{s.ipa}</div>
              </button>
            ))}
          </div>
        )}

        {/* Escuchar palabra */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 10 }}>
          <button className="drill-listen" onClick={playFull}>
            {wordAudio.state === 'playing' ? (
              <div className="bars playing" style={{ height: 12, color: 'var(--lime)' }}>
                <span />
                <span />
                <span />
                <span />
              </div>
            ) : (
              <IconPlay size={10} />
            )}
            <span>{wordAudio.state === 'playing' ? 'Reproduciendo…' : 'Escuchar palabra'}</span>
          </button>
        </div>

        {/* Feedback */}
        <div className="drill-feedback">
          {phase === 'recording' && (
            <div className="drill-status">
              <div className="listen-bars" style={{ height: 38, transform: 'scale(0.7)' }}>
                <span /><span /><span /><span /><span /><span /><span />
              </div>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Escuchando…</div>
            </div>
          )}
          {phase === 'scoring' && (
            <div className="drill-status">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontFamily: 'JetBrains Mono, monospace', fontSize: 13 }}>
                <div className="dots-anim spinner" />
                Analizando<span className="dots-anim" />
              </div>
            </div>
          )}
          {(phase === 'feedback' || done) && lastAttempt && (
            <div className={`drill-score ${toneFor(lastAttempt.score)}`}>
              <div className="drill-score-num">
                {lastAttempt.score}
                <span>%</span>
              </div>
              <div className="drill-score-label">{lastAttempt.label}</div>
            </div>
          )}
          {phase === 'idle' && attempts.length === 0 && !error && (
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'center', lineHeight: 1.5, maxWidth: 240 }}>
              Escuchá primero, luego tocá el micrófono y repetí la palabra.
            </div>
          )}
          {error && (
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#fca5a5', textAlign: 'center', maxWidth: 260 }} role="alert">
              {error}
            </div>
          )}
        </div>

        {/* Pips de intentos */}
        <div className="drill-attempts">
          {Array.from({ length: TOTAL_ATTEMPTS }).map((_, i) => {
            const a = attempts[i];
            const isNext = i === attempts.length && !done;
            return (
              <div key={i} className={`drill-pip ${a ? toneFor(a.score) : ''} ${isNext ? 'next' : ''}`}>
                {a ? a.score : i + 1}
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div style={{ padding: '14px 22px 22px', display: 'flex', justifyContent: 'center' }}>
          {done ? (
            <button className="drill-mic-btn drill-done-btn" onClick={onClose}>
              <IconCheck size={20} />
              <span style={{ fontFamily: 'Caveat, cursive', fontSize: 22, fontWeight: 600 }}>Listo · {best}%</span>
            </button>
          ) : (
            <button
              className={`drill-mic-btn ${phase === 'recording' ? 'recording' : ''}`}
              onClick={() => {
                if (mic.state === 'recording') mic.stop();
                else void startRecord();
              }}
              disabled={phase === 'scoring'}
              aria-label={mic.state === 'recording' ? 'Detener' : 'Grabar'}
            >
              {phase === 'recording' ? (
                <div className="bars playing" style={{ height: 22, color: 'white' }}>
                  <span style={{ width: 3 }} />
                  <span style={{ width: 3 }} />
                  <span style={{ width: 3 }} />
                  <span style={{ width: 3 }} />
                </div>
              ) : (
                <IconMicSolid size={26} />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
